import { JMCButton } from "@components/JMCButton/JMCButton";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { ImageQueryResponse } from "@jmc/core/src/types/ImageQueryReponse";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Card, ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { CarouselSlide as SolidCarouselSlide } from "@jmc/solid-design-system/src/components/organisms/ImageCarousel/CarouselSlide/CarouselSlide";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import {
    CarouselAlignContent,
    CarouselAlignContentVertically,
    CarouselBackgroundColor,
    CarouselSize,
    CarouselTextColor,
    CarouselViewMode,
    CMSButtonProps,
    CMSFootnotes,
} from "@types";
import classnames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { Fragment } from "react";

import style from "./style.module.scss";

export type Colors = "primary" | "secondary" | "accent" | "success" | "warning" | "error" | "grey" | "transparent";

interface Props {
    size?: CarouselSize;
    title: string;
    text: string;
    image: ImageQueryResponse;
    buttons: CMSButtonProps[];
    viewMode: CarouselViewMode;
    titleColor?: CarouselTextColor;
    textColor?: CarouselTextColor;
    backgroundColor?: CarouselBackgroundColor;
    alignContent?: CarouselAlignContent;
    alignContentVertically?: CarouselAlignContentVertically;
    imageFocus?: ImagePosition;
    overlay?: "none" | "dark" | "light";
    placement?: string;
    footnotes?: CMSFootnotes[];
    loading?: "lazy" | "eager";
    headerVariant?: boolean;
}

export const CarouselSlide = ({
    size = CarouselSize.MEDIUM,
    title,
    text,
    image,
    buttons,
    viewMode,
    titleColor = CarouselTextColor.DARK,
    textColor = CarouselTextColor.DARK,
    backgroundColor = CarouselBackgroundColor.WHITE,
    alignContent = CarouselAlignContent.START,
    alignContentVertically = CarouselAlignContentVertically.CENTER,
    imageFocus = ImagePosition.CENTER_CENTER,
    overlay = "none",
    placement,
    footnotes,
    loading = "lazy",
    headerVariant = false,
}: Props): JSX.Element => {
    const getOverlay = (): string => {
        if (overlay === "dark") {
            return "background: adjust-color(#fff, $alpha: -0.7)";
        }
        if (overlay === "light") {
            return "adjust-color(#222, $alpha: -0.7)";
        }
        return;
    };
    const { jnjFullBranded } = useJnjBranding();
    const isMobile = useMediaQuery(BreakPoint.md);

    const accessoryRight = jnjFullBranded ? <Card.CTAIconArrow /> : null;

    const slideText = (
        <>
            {title && (
                <div
                    className={classnames(
                        style.title,
                        !jnjFullBranded && style[`color-${titleColor?.toLowerCase()?.replace(" ", "-")}`],
                    )}
                >
                    <Typography
                        variant={jnjFullBranded ? (headerVariant ? "h1" : "h4") : "h3"}
                        color={jnjFullBranded ? "dark" : "inherit"}
                        data-test-id="CarouselSlide.Title"
                    >
                        <JMCHtml color="inherit" footnotes={footnotes} placement={placement}>
                            {title}
                        </JMCHtml>
                    </Typography>
                </div>
            )}
            <div
                className={classnames(style.description, style[`color-${textColor?.toLowerCase()?.replace(" ", "-")}`])}
                data-test-id="CarouselSlide.Text"
            >
                {jnjFullBranded ? (
                    <Typography
                        variant={headerVariant ? "body-01" : "body-03"}
                        color={headerVariant ? "dark" : "light"}
                    >
                        <JMCHtml color="inherit" footnotes={footnotes}>
                            {text}
                        </JMCHtml>
                    </Typography>
                ) : (
                    <JMCHtml color="inherit" footnotes={footnotes}>
                        {text}
                    </JMCHtml>
                )}
            </div>
            {buttons?.length > 0 && (
                <div className={classnames(style.buttons, headerVariant && style.headerVariant)}>
                    {buttons.map((button: CMSButtonProps, index: number) => {
                        return (
                            <JMCButton
                                key={index}
                                button={
                                    jnjFullBranded
                                        ? headerVariant
                                            ? {
                                                  ...button,
                                                  color: "Primary Color",
                                                  variant: index === 0 ? "container" : "Naked",
                                              }
                                            : {
                                                  ...button,
                                                  color: "Primary Color",
                                                  flexibleWidth: true,
                                                  variant: "Naked",
                                                  noSpacing: true,
                                              }
                                        : button
                                }
                                placement={placement}
                                accessoryRight={!headerVariant && accessoryRight}
                                fullWidth={jnjFullBranded && headerVariant && isMobile}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );

    const slideImage = (
        <GatsbyImage
            data-test-id="CarouselSlide.Image"
            alt={image?.description || ""}
            image={image?.gatsbyImageData}
            style={
                jnjFullBranded
                    ? {
                          top: 0,
                          insetInlineStart: 0,
                          paddingBlockStart: "75%",
                          height: 0,
                      }
                    : {
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          top: 0,
                          insetInlineStart: 0,
                      }
            }
            imgStyle={{ objectPosition: imageFocus?.replace("-", " ") }}
            loading={loading}
        />
    );

    if (jnjFullBranded) {
        return (
            <SolidCarouselSlide
                title={title}
                image={image?.gatsbyImageData && slideImage}
                viewMode={viewMode}
                headerVariant={headerVariant}
            >
                {slideText}
            </SolidCarouselSlide>
        );
    }

    return (
        <div
            className={classnames(
                style.carouselSlide,
                style[`size-${size.toLowerCase()}`],
                style[viewMode?.toLowerCase()],
                style[`backgroundColor-${backgroundColor?.replace(" ", "-")?.toLowerCase()}`],
            )}
            data-test-id={`CarouselSlide.${title}`}
        >
            <div
                className={classnames(
                    style.text,
                    style[`horizontalAlign-${alignContent?.toLowerCase()}`],
                    style[`verticalAlign-${alignContentVertically?.toLowerCase()}`],
                )}
            >
                {slideText}
            </div>
            <div className={classnames(style.image)}>
                {image?.gatsbyImageData ? (
                    <Fragment>
                        {slideImage}
                        {overlay !== "none" && (
                            <div className={classnames(style.overlay, style[`overlay_${overlay}`])} />
                        )}
                    </Fragment>
                ) : (
                    <div
                        className={style.backgroundImageSVG}
                        style={{
                            backgroundImage: getOverlay()
                                ? `${getOverlay()}, url('${image?.url}')`
                                : `url('${image?.url}')`,
                        }}
                        data-test-id="CarouselSlide.Image"
                    />
                )}
            </div>
        </div>
    );
};
